import { cn } from '@/lib/utils/cn';
import { SrTable } from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * RankedBars — one horizontal bar per item in rank order, for a score out
 * of `max` (T-0272, E24-F02: the vendor fit index). The items the reader
 * should look at first (`emphasis`, the top three) draw in ink-900, the
 * rest in ink-300; the rank mark sits before the label and the value at the
 * bar's end. An item with no value (no confirmed rating yet) keeps its row
 * and says so. Ships a visually hidden table; the bars themselves are an
 * image. Theme tokens only.
 */
export interface RankedBar {
  key: string;
  /** "1", "2=" for a shared rank; null when unranked. */
  rank: string | null;
  label: string;
  /** A line under the label ("coverage 76.6% · 12 of 15 modules confirmed"). */
  note?: string | null;
  /** 0..max; null when there is nothing to draw. */
  value: number | null;
  emphasis?: boolean;
}

export interface RankedBarsProps {
  bars: readonly RankedBar[];
  /** The scale's end; the index runs to 100. */
  max?: number;
  /** What the value is ("Index"), for the table and the labels. */
  valueLabel?: string;
  /** The hidden table's caption. */
  caption: string;
  /** What an empty row says. */
  emptyText?: string;
  motionKey?: string | number;
  className?: string;
}

export function RankedBars({
  bars,
  max = 100,
  valueLabel = 'Index',
  caption,
  emptyText = 'No confirmed rating yet',
  motionKey,
  className,
}: RankedBarsProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const rows = bars.map((b) => [
    b.rank ?? '–',
    b.label,
    b.value === null ? emptyText : b.value.toFixed(1),
  ]);
  return (
    <div key={runKey} className={cn('relative', animate && 'chart-anim', className)}>
      <ol aria-hidden="true" className="m-0 flex list-none flex-col gap-1.5 p-0">
        {bars.map((b) => {
          const pct = b.value === null ? 0 : Math.max(0, Math.min(100, (b.value / max) * 100));
          return (
            <li
              key={b.key}
              data-emphasis={b.emphasis ? 'true' : undefined}
              className="grid grid-cols-[28px_minmax(0,1fr)_56px] items-center gap-x-3"
            >
              <span className="font-mono text-meta tabular-nums text-ink-500">{b.rank ?? '–'}</span>
              <span className="flex min-w-0 flex-col gap-1">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span
                    className={cn(
                      'truncate text-body-sm',
                      b.emphasis ? 'font-semibold text-ink-900' : 'text-ink-700',
                    )}
                  >
                    {b.label}
                  </span>
                  {b.note ? (
                    <span className="truncate text-meta text-ink-500">{b.note}</span>
                  ) : null}
                </span>
                <span className="relative block h-2.5 overflow-hidden rounded bg-paper-100">
                  {b.value === null ? null : (
                    <i
                      className={cn(
                        'a-grow absolute inset-y-0 left-0 block rounded-r-[3px]',
                        b.emphasis ? 'bg-ink-900' : 'bg-ink-300',
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  )}
                </span>
              </span>
              <span
                className={cn(
                  'text-right font-mono text-body-sm tabular-nums',
                  b.value === null
                    ? 'text-meta text-ink-500'
                    : b.emphasis
                      ? 'font-semibold text-ink-900'
                      : 'text-ink-700',
                )}
              >
                {b.value === null ? '–' : b.value.toFixed(1)}
              </span>
            </li>
          );
        })}
      </ol>
      <SrTable caption={caption} head={['Rank', 'Vendor', valueLabel]} rows={rows} />
    </div>
  );
}
